import React, { useState, useEffect, useCallback } from "react";
import styles from "./PaymentPlanList.module.sass";
import cn from "classnames";
import Card from "../../components/Card";
import Form from "../../components/Form";
import Filters from "../../components/Filters";
import Table from "./Table";
import Panel from "./Panel";
import Icon from "../../components/Icon";
import { useNavigate } from "react-router-dom";
import { getCompanyOffers, deleteOffer } from "../../services/offerAdmin.service";
import { toast } from "react-toastify";
import Settings from "./Settings";

const JobOfferList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState(false);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    sorting: "Plus récentes",
    contractTypes: [],
    statuses: []
  });

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      const options = {
        page: currentPage,
        perPage: 10,
        sort: "-created",
        search: search.trim(),
        contractTypes: filters.contractTypes,
        statuses: filters.statuses,
        sorting: filters.sorting
      };

      const result = await getCompanyOffers(options);
      
      if (result.success) {
        // Traiter les données des offres d'alternance
        const processedOffers = result.data.map(offer => {
          // Calculer le nombre de candidatures
          const candidatesCount = offer.candidats_ids ? offer.candidats_ids.length : 0;
          
          // Formater la date de début
          const startDate = offer.start_date ? new Date(offer.start_date).toLocaleDateString('fr-FR') : 'Non définie';
          
          // Déterminer le statut avec une couleur
          let statusColor = "gray";
          if (offer.status === "active") {
            statusColor = "green";
          } else if (offer.status === "closed") {
            statusColor = "red";
          } else if (offer.status === "draft") {
            statusColor = "orange";
          }
          
          return {
            ...offer,
            candidatesCount,
            startDate,
            statusColor,
            formattedStatus: offer.status === "active" ? "Active" : 
                           offer.status === "closed" ? "Fermée" : 
                           offer.status === "draft" ? "Brouillon" : offer.status
          };
        });
        
        setOffers(processedOffers || []);
        setTotalPages(result.totalPages || 1);
        setNoResults(processedOffers.length === 0 && !!search);
      } else {
        throw new Error(result.error);
      }

    } catch (err) {
      console.error("Erreur lors du chargement des offres:", err);
      setError("Impossible de charger les offres d'alternance. Veuillez réessayer.");
      setOffers([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [search, currentPage, filters]);
  
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const handleSearchChange = (value) => {
    setSearch(value);
  };
  
  const handleSubmit = (e) => {
    e?.preventDefault();
    setCurrentPage(1);
    fetchOffers();
  };
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteOffers = async () => {
    if (selectedOffers.length === 0) return;
    
    setIsDeleting(true);
    
    try {
      for (const offerId of selectedOffers) {
        const result = await deleteOffer(offerId);
        if (!result.success) {
          throw new Error(result.error);
        }
      }
      
      toast.success(`${selectedOffers.length} offre(s) supprimée(s) avec succès`);
      setSelectedOffers([]);
      
      // Recharger les offres
      fetchOffers();
    } catch (error) {
      console.error('Error deleting offers:', error);
      toast.error('Une erreur est survenue lors de la suppression des offres');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateOffer = () => {
    navigate('/company/alternance-offers/add');
  };

  // Gestionnaire pour les changements de filtres
  const handleFiltersChange = (newFilters) => {
    console.log('Nouveaux filtres appliqués:', newFilters);
    setFilters(newFilters);
    setCurrentPage(1); // Remettre à la première page lors du changement de filtres
  };

  return (
    <>
      <Card
        className={styles.card}
        title="Offres d'emploi"
        classTitle={cn("title-green", styles.title)}
        classCardHead={cn(styles.head, { [styles.hidden]: visible })}
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={handleSearchChange}
              onSubmit={handleSubmit}
              placeholder="Rechercher par titre, description ou lieu"
              type="text"
              name="search"
              icon="search"
            />
            <Filters
              className={styles.filters}
              title="Filtrer"
            >
              <Settings onFiltersChange={handleFiltersChange} />
            </Filters>

          </>
        }
      >

        
        {loading ? (
          <div className={styles.loading}>Chargement des offres d'alternance...</div>
        ) : offers.length === 0 ? (
          <div className={styles.empty}>
            {noResults ? (
              <p>Aucune offre trouvée pour votre recherche "{search}".</p>
            ) : (
              <p>Aucune offre d'alternance disponible.</p>
            )}
          </div>
        ) : (
          <div className={cn(styles.row, { [styles.flex]: visible })}>
            <Table
              className={styles.table}
              activeTable={visible}
              setActiveTable={setVisible}
              items={offers}
              onSelectionChange={setSelectedOffers}
            />
            
            <div className={styles.foot}>
              <button 
                className={styles.arrow} 
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
                disabled={currentPage === 1}
              >
                <Icon name="arrow-left" size="20" />
              </button>
              <span className={styles.pagination}>
                Page {currentPage} sur {totalPages}
              </span>
              <button 
                className={styles.arrow} 
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
                disabled={currentPage === totalPages}
              >
                <Icon name="arrow-right" size="20" />
              </button>
            </div>
          </div>
        )}
      </Card>
      <Panel 
        selectedCount={selectedOffers.length}
        onDelete={handleDeleteOffers}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default JobOfferList;